import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import './QRCodeScanner.scss';

interface QRCodeScannerProps {
  onScan: (decodedText: string) => void;
  onError?: (error: string) => void;
  facingMode?: 'user' | 'environment';
  qrCodeSuccessCallback?: (decodedText: string, decodedResult: any) => void;
  qrCodeErrorCallback?: (error: string) => void;
  verbose?: boolean;
  paused?: boolean; // When true, scanner is paused/stopped
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScan,
  onError,
  facingMode = 'environment',
  qrCodeSuccessCallback,
  qrCodeErrorCallback,
  verbose = false,
  paused = false,
}) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const scannerIdRef = useRef(`qr-scanner-${Math.random().toString(36).substr(2, 9)}`);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const isMountedRef = useRef(true);
  const isStartingRef = useRef(false);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleScan = useCallback(async (decodedText: string, decodedResult: any) => {
    if (verbose) {
      console.log(`QR Code detected: ${decodedText}`);
    }
    
    // Stop scanner immediately when QR code is detected
    if (html5QrCodeRef.current) {
      try {
        // Check if element still exists before stopping
        const scannerElement = document.getElementById(scannerIdRef.current);
        if (scannerElement && scannerElement.parentNode) {
          await html5QrCodeRef.current.stop();
        }
        setIsScanning(false);
      } catch (e: any) {
        // Suppress expected cleanup errors
        const errorMessage = e?.message || '';
        const isExpectedError = 
          errorMessage.includes('removeChild') ||
          errorMessage.includes('not of type Node') ||
          errorMessage.includes('AbortError') ||
          errorMessage.includes('media was removed');
        
        if (!isExpectedError) {
          console.error('Error stopping scanner:', e);
        }
        setIsScanning(false);
      }
    }
    
    onScan(decodedText);
    if (qrCodeSuccessCallback) {
      qrCodeSuccessCallback(decodedText, decodedResult);
    }
  }, [onScan, qrCodeSuccessCallback, verbose]);

  const handleError = useCallback((errorMessage: string) => {
    // Ignore expected errors that occur during normal scanning
    // These are not actual errors - they just mean no QR code was detected yet
    const expectedErrors = [
      'No QR code found',
      'NotFoundException',
      'No MultiFormat Readers were able to detect the code',
      'QR code parse error',
    ];
    
    const isExpectedError = expectedErrors.some(expected => 
      errorMessage && errorMessage.includes(expected)
    );
    
    if (!isExpectedError) {
      if (verbose) {
        console.warn(`QR Code error: ${errorMessage}`);
      }
      if (qrCodeErrorCallback) {
        qrCodeErrorCallback(errorMessage);
      }
      if (onError) {
        onError(errorMessage);
      }
    }
  }, [onError, qrCodeErrorCallback, verbose]);

  useEffect(() => {
    isMountedRef.current = true;
    let scannerInstance: Html5Qrcode | null = null;

    // Stop scanner if paused
    if (paused && html5QrCodeRef.current && isScanning) {
      const stopScanner = async () => {
        try {
          const scannerElement = document.getElementById(scannerIdRef.current);
          if (scannerElement && scannerElement.parentNode) {
            await html5QrCodeRef.current!.stop();
          }
        } catch (err: any) {
          // Suppress expected cleanup errors
          const errorMessage = err?.message || '';
          const isExpectedError = 
            errorMessage.includes('removeChild') ||
            errorMessage.includes('not of type Node') ||
            errorMessage.includes('AbortError') ||
            errorMessage.includes('media was removed') ||
            err?.name === 'AbortError';
          
          if (!isExpectedError) {
            console.warn('Error stopping paused scanner:', err);
          }
        }
      };
      stopScanner();
      setIsScanning(false);
      return;
    }

    // Don't start if paused
    if (paused) {
      return;
    }

    const startScanning = async () => {
      if (!scannerRef.current || !isMountedRef.current || isStartingRef.current) return;
      
      isStartingRef.current = true;

      // Stop any existing scanner first
      if (html5QrCodeRef.current) {
        try {
          const scannerElement = document.getElementById(scannerIdRef.current);
          if (scannerElement && scannerElement.parentNode) {
            await html5QrCodeRef.current.stop();
          }
        } catch (e: any) {
          // Suppress expected cleanup errors
          const errorMessage = e?.message || '';
          const isExpectedError = 
            errorMessage.includes('removeChild') ||
            errorMessage.includes('not of type Node') ||
            errorMessage.includes('AbortError') ||
            errorMessage.includes('media was removed') ||
            e?.name === 'AbortError';
          
          if (!isExpectedError) {
            console.warn('Error stopping existing scanner:', e);
          }
        }
        html5QrCodeRef.current = null;
      }

      // Wait for container to be properly sized
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (!scannerRef.current || !isMountedRef.current) {
        isStartingRef.current = false;
        return;
      }

      // Use a fixed safe size to avoid calculation issues
      // The library requires minimum 50px, we use 250px which is safe and performs well
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        disableFlip: false,
      };

      try {
        const html5QrCode = new Html5Qrcode(scannerIdRef.current);
        scannerInstance = html5QrCode;
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: facingMode },
          config,
          handleScan,
          handleError
        );

        if (isMountedRef.current) {
          setIsScanning(true);
          setError(null);
          setCameraPermission('granted');
          isStartingRef.current = false;
        }
      } catch (err: any) {
        isStartingRef.current = false;
        if (!isMountedRef.current) return;
        
        const errorMessage = err.message || 'Failed to start camera';
        setError(errorMessage);
        setIsScanning(false);
        
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCameraPermission('denied');
        }
        
        if (onError) {
          onError(errorMessage);
        }
        console.error('Error starting QR scanner:', err);
      }
    };

    startScanning();

    return () => {
      isMountedRef.current = false;
      const cleanup = async () => {
        if (scannerInstance) {
          try {
            // Check if the scanner element still exists in the DOM
            const scannerElement = document.getElementById(scannerIdRef.current);
            if (scannerElement && scannerElement.parentNode) {
              // Only stop if the element is still in the DOM
              await scannerInstance.stop();
            } else {
              // Element already removed, just clear the reference
              // Try to stop without DOM manipulation
              try {
                // Use clear() method if available, which doesn't manipulate DOM
                if (typeof (scannerInstance as any).clear === 'function') {
                  (scannerInstance as any).clear();
                }
              } catch (clearErr) {
                // Ignore clear errors
              }
            }
          } catch (err: any) {
            // Suppress specific errors that occur when DOM is already cleaned up
            const errorMessage = err?.message || '';
            const isExpectedError = 
              errorMessage.includes('removeChild') ||
              errorMessage.includes('not of type Node') ||
              errorMessage.includes('AbortError') ||
              errorMessage.includes('media was removed') ||
              err?.name === 'AbortError';
            
            if (!isExpectedError) {
              console.error('Error stopping QR scanner:', err);
            }
          } finally {
            scannerInstance = null;
            html5QrCodeRef.current = null;
          }
        }
      };
      cleanup();
    };
  }, [facingMode, handleScan, handleError, onError, paused]);

  const handleStop = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        // Check if element still exists before stopping
        const scannerElement = document.getElementById(scannerIdRef.current);
        if (scannerElement && scannerElement.parentNode) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current = null;
        setIsScanning(false);
      } catch (err: any) {
        // Suppress expected cleanup errors
        const errorMessage = err?.message || '';
        const isExpectedError = 
          errorMessage.includes('removeChild') ||
          errorMessage.includes('not of type Node') ||
          errorMessage.includes('AbortError') ||
          errorMessage.includes('media was removed') ||
          err?.name === 'AbortError';
        
        if (!isExpectedError) {
          console.error('Error stopping scanner:', err);
        }
        html5QrCodeRef.current = null;
        setIsScanning(false);
      }
    }
  };

  return (
    <div className="qr-code-scanner">
      <div id={scannerIdRef.current} ref={scannerRef} className="qr-scanner-container" />
      {error && (
        <div className="scanner-error">
          <strong>Error:</strong> {error}
          {cameraPermission === 'denied' && (
            <p className="mt-2 mb-0">
              Please allow camera access in your browser settings and refresh the page.
            </p>
          )}
        </div>
      )}
      {!isScanning && !error && (
        <div className="scanner-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Starting camera...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeScanner;

